import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { ProfessionalEntity } from '../../store/entities/state';
import { getProfessionalAppointmentsAsync, getExpensesAsync } from '../../ajax/requests';
import { ApplicationState } from '../../store';
import { AppointmentData, ExpenseData } from '../../models';
import { Modal, ModalBody, ModalHeader, ListGroup, ListGroupItem } from 'reactstrap';
import StyledTable from '../../components/StyledTable';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { formatEuros } from '../../shared/string';
import { getPaymentsSettings, getExtrasSettings } from '../../store/settings/selectors';
import { getTotalForProfessional } from '../../shared/appointmint/payments';
import { getActualTotalPrice } from '../../shared/appointmint/appointment';
import { entityCreating, openEntity } from '../../store/entities/actions';
import { Guid } from '../../shared/utils';
import ProfessionalSalaryModal from '../Billing/ProfessionalSalaryModal';
import { getExpenseDescription } from '../Billing/Expenses';
import AppButton from '../../components/AppButton';

export interface ProfessionalFeesTableProps {
    isOpen: boolean;
    professional: ProfessionalEntity;
}

interface MonthRow {
    month: string;
    appointments: AppointmentData[];
    toPay: number;
    totalProduced: number;
    summary: string[];
    paid: number;
    expenses: ExpenseData[];
}

const ProfessionalFeesTable: React.FC<ProfessionalFeesTableProps> = ({ isOpen, professional }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const _paymentProfiles = useSelector(getPaymentsSettings);
    const paymentProfiles = React.useMemo(
        () => Object.fromEntries(_paymentProfiles.map(p => [p.id, p])),
        [_paymentProfiles],
    );
    const _extrasConfig = useSelector(getExtrasSettings);
    const extrasConfig = React.useMemo(
        () => Object.fromEntries(_extrasConfig.map(p => [p.id, p])),
        [_extrasConfig],
    );

    const expenseChangingHistory = useSelector(
        (state: ApplicationState) => state.entities.expenses.changingHistory,
    );

    const [rows, setRows] = React.useState<MonthRow[]>([]);
    const [isLoading, setLoading] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState<MonthRow | undefined>();
    const [selectedExpensesMonth, setSelectedExpensesMonth] = React.useState<string | undefined>();
    const expensesToShow = React.useMemo(
        () => selectedExpensesMonth ? (rows.find(r => r.month === selectedExpensesMonth)?.expenses ?? []) : [],
        [selectedExpensesMonth, rows],
    );

    React.useEffect(() => {
        if (!isOpen) return;

        const from = moment().subtract(5, 'years').format('YYYY-MM-DD');
        const to = moment().format('YYYY-MM-DD');

        setLoading(true);
        Promise.all([
            getProfessionalAppointmentsAsync(professional.id, 5000),
            getExpensesAsync(from, to, professional.id),
        ]).then(([appointments, expenses]) => {

            const byMonth = appointments.reduce<Record<string, AppointmentData[]>>((acc, a) => {
                const month = a.date.substring(0, 7);
                return {...acc, [month]: [...(acc[month] ?? []), a]};
            }, {});

            const expensesByMonth = expenses.reduce<Record<string, ExpenseData[]>>((acc, e) => {
                const month = (e.payment.date ?? '').substring(0, 7);
                if (!month) return acc;
                return {...acc, [month]: [...(acc[month] ?? []), e]};
            }, {});

            const allMonths = [...new Set([...Object.keys(byMonth), ...Object.keys(expensesByMonth)])].sort().reverse();

            setRows(allMonths.map(month => {
                const monthAppointments = byMonth[month] ?? [];
                const monthExpenses = expensesByMonth[month] ?? [];
                const [toPay, summary] = getTotalForProfessional(monthAppointments, professional, paymentProfiles, extrasConfig);
                const totalProduced = monthAppointments.reduce((sum, a) => sum + getActualTotalPrice(a), 0);
                const paid = monthExpenses.reduce((sum, e) => sum + (e.payment.value ?? 0), 0);
                return {month, appointments: monthAppointments, toPay, totalProduced, summary, paid, expenses: monthExpenses};
            }));
        }).finally(() => setLoading(false));
    }, [isOpen, professional.id, expenseChangingHistory]);

    const onCreateExpense = React.useCallback(() => {
        if (!selectedRow) return;
        const from = `${selectedRow.month}-01`;
        const to = moment(from).endOf('month').format('YYYY-MM-DD');
        dispatch(entityCreating<ExpenseData>('expenses', {
            id: Guid.newGuid(),
            entity: t(TK.professional),
            name: `${t(TK.salary)} - ${professional.name}`,
            notes: `${t(TK.from)} ${from} - ${to}`,
            type: t(TK.salary),
            payment: {value: selectedRow.toPay, type: '', date: moment().format('YYYY-MM-DD')},
            attachments: [],
            professionalId: professional.id,
            professionalName: professional.name,
        }));
        setSelectedRow(undefined);
    }, [dispatch, professional, selectedRow, t]);

    const handleOpenExpense = React.useCallback((expense: ExpenseData) => {
        setSelectedExpensesMonth(undefined);
        dispatch(openEntity('expenses', expense.id, () => Promise.resolve(expense)));
    }, [dispatch]);

    if (isLoading) return <span>{t(TK.loading)}...</span>;

    return (
        <>
            <StyledTable scrollable hover>
                <thead>
                    <tr>
                        <th>{t(TK.month)}</th>
                        <th>{t(TK.appointments)}</th>
                        <th>{t(TK.toPay)}</th>
                        <th>{t(TK.paid)}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={4} style={{textAlign: 'center', color: 'var(--text-muted)'}}>{t(TK.noDataAvailable)}</td></tr>
                    ) : rows.map(row => (
                        <tr key={row.month}>
                            <td style={{textTransform: 'capitalize'}}>{moment(row.month, 'YYYY-MM').format('MMMM YYYY')}</td>
                            <td>{row.appointments.length}</td>
                            <td>
                                <AppButton color="link" style={{padding: 0}} onClick={() => setSelectedRow(row)}>
                                    {formatEuros(row.toPay)}
                                </AppButton>
                            </td>
                            <td>
                                {row.expenses.length > 0 ? (
                                    <AppButton color="link" style={{padding: 0}} onClick={() => setSelectedExpensesMonth(row.month)}>
                                        {formatEuros(row.paid)}
                                    </AppButton>
                                ) : formatEuros(row.paid)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </StyledTable>

            {selectedRow && (
                <ProfessionalSalaryModal
                    isOpen
                    onClose={() => setSelectedRow(undefined)}
                    professionalName={professional.name}
                    professionalParcel={selectedRow.toPay}
                    totalProduced={selectedRow.totalProduced}
                    summary={selectedRow.summary}
                    appointments={selectedRow.appointments}
                    onCreateExpense={onCreateExpense}
                />
            )}

            <Modal size='lg' isOpen={!!selectedExpensesMonth} toggle={() => setSelectedExpensesMonth(undefined)}>
                <ModalHeader toggle={() => setSelectedExpensesMonth(undefined)}>{t(TK.expenses)}</ModalHeader>
                <ModalBody>
                    <ListGroup>
                        {expensesToShow.map(e => (
                            <ListGroupItem
                                key={e.id}
                                style={{cursor: 'pointer'}}
                                onClick={() => handleOpenExpense(e)}
                            >
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <div>
                                        <div><b>{e.name} ({e.type})</b></div>
                                        <div>{getExpenseDescription(e, t)}</div>
                                    </div>
                                    <div style={{textAlign: 'right'}}>
                                        <b>{formatEuros(e.payment.value)}</b>
                                        {!!e.payment.vat && (
                                            <div style={{fontSize: '0.78rem', color: 'var(--text-muted)'}}>
                                                {t(TK.vat)}: {formatEuros(e.payment.value - e.payment.value / (1 + e.payment.vat))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                </ModalBody>
            </Modal>
        </>
    );
};

export default ProfessionalFeesTable;
